import type {
  AssetId,
  BeatId,
  Tick,
} from "./ids.js";

/**
 * Render modes: three bakeoff POCs plus production real-time 3D (R3F).
 * Campaign packs may ship only the production mode; bakeoff coverage uses
 * {@link BAKEOFF_RENDER_MODES} and does not require `realtime-3d`.
 */
export type RenderMode =
  | "hybrid-video"
  | "layered-2d"
  | "lightweight-3d"
  | "realtime-3d";

/** JSON-safe values for POC-specific adapter config. */
export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

/**
 * One media track bound to a semantic beat.
 * A beat may have multiple bindings (environment, actor, prop, etc.).
 * Ranges are integer ticks within the bound asset's source timeline.
 */
export interface BeatMediaBinding {
  /** Stable id unique within its RenderVariant. */
  bindingId: string;
  beatId: BeatId;
  /** Inclusive start tick in the bound asset. */
  sourceStartTick: Tick;
  /** Exclusive end tick in the bound asset. */
  sourceEndTick: Tick;
  /** Asset for this track; must appear in the variant's requiredAssetIds. */
  assetId: AssetId;
  /** Optional layer id (layered-2d). */
  layerId?: string;
  /** Optional animation / clip name (2d/3d/video). */
  animationId?: string;
  /** Optional subclip id within a longer hybrid master. */
  clipId?: string;
}

/**
 * One renderer-consumable presentation of a SourcePack.
 * Adapters select by `mode` (and optional `id`).
 *
 * Packs must include at least one variant. Bakeoff fixtures that exercise all
 * three POCs should additionally pass `assertBakeoffRenderModeCoverage`.
 */
export interface RenderVariant {
  id: string;
  mode: RenderMode;
  label: string;
  /** Assets that must be loadable before preview/export. */
  requiredAssetIds: AssetId[];
  /**
   * One or more bindings per canonical beat.
   * Every canonical beat needs ≥1 binding; bindingIds unique within the variant.
   */
  beatBindings: BeatMediaBinding[];
  /** POC-specific knobs; must be JSON-serializable plain data. */
  config: { [key: string]: JsonValue };
}

export const RENDER_MODES: readonly RenderMode[] = [
  "hybrid-video",
  "layered-2d",
  "lightweight-3d",
  "realtime-3d",
] as const;

/** POC bakeoff modes only — ordinary campaign packs need not cover these. */
export const BAKEOFF_RENDER_MODES = [
  "hybrid-video",
  "layered-2d",
  "lightweight-3d",
] as const satisfies readonly RenderMode[];
